/**
 * @fileoverview
 * Showcase/Examples section — isometric city map with positioned pill badges.
 *
 * Content:
 * - Section eyebrow: "Examples"
 * - Section heading: "Click to explore AI-generated renders"
 * - Isometric city map illustration (inline SVG)
 * - 6 pill badges positioned over the map, each opening a lightbox on click
 * - Lightbox with prev/next navigation, metadata, counter, dismiss
 *
 * Design:
 * - Matches live aideco.us: isometric map background with white pill buttons
 * - Keyboard accessible (Escape, ArrowLeft, ArrowRight in lightbox)
 * - No useEffect — keyboard handled via onKeyDown, scroll lock in callbacks
 */

"use client";

import { ChevronLeftIcon, ChevronRightIcon, XIcon } from "lucide-react";
import Image from "next/image";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useCallback, useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";

import { SectionShell, SectionContainer } from "../primitives";

interface ShowcaseItem {
  id: string;
  label: string;
  title: string;
  description: string;
  image: string;
  /** Absolute position on the map as CSS percentage values */
  position: { top: string; left: string };
}

const showcaseItemSeeds = [
  {
    id: "cliff-house",
    label: "Cliff House",
    title: "Cliff House",
    description: "Modern cantilevered architecture perched on dramatic coastline cliffs",
    image: "/images/showcase/cliff-house.png",
    position: { top: "18%", left: "65%" },
  },
  {
    id: "brutalist-tower",
    label: "Brutalist Tower",
    title: "Brutalist Tower",
    description: "Dramatic concrete architecture with geometric forms",
    image: "/images/showcase/brutalist-tower.png",
    position: { top: "45%", left: "12%" },
  },
  {
    id: "minimalist-loft",
    label: "Minimalist Loft",
    title: "Minimalist Loft",
    description: "Clean Scandinavian-inspired interior with natural light",
    image: "/images/showcase/minimalist-loft.png",
    position: { top: "45%", left: "75%" },
  },
  {
    id: "glass-pavilion",
    label: "Glass Pavilion",
    title: "Glass Pavilion",
    description: "Transparent walls blending indoor and outdoor spaces",
    image: "/images/showcase/glass-pavilion.png",
    position: { top: "18%", left: "18%" },
  },
  {
    id: "atrium-lobby",
    label: "Atrium Lobby",
    title: "Atrium Lobby",
    description: "Double-height entrance with dramatic vertical space",
    image: "/images/showcase/atrium-lobby.png",
    position: { top: "72%", left: "32%" },
  },
  {
    id: "urban-courtyard",
    label: "Urban Courtyard",
    title: "Urban Courtyard",
    description: "Green oasis in dense urban environment",
    image: "/images/showcase/urban-courtyard.png",
    position: { top: "72%", left: "72%" },
  },
 ] as const;

/* ------------------------------------------------------------------ */
/*  Isometric city map — live site image                              */
/* ------------------------------------------------------------------ */

function IsometricCityMap({ ariaLabel }: { ariaLabel: string }) {
  return (
    <div
      className="relative aspect-[2048/948] w-full overflow-hidden rounded-2xl"
      role="img"
      aria-label={ariaLabel}
    >
      <Image
        src="/images/showcase/isometric-city-map.avif"
        alt={ariaLabel}
        fill
        className="object-contain"
        priority
        sizes="(max-width: 768px) 100vw, (max-width: 1280px) 90vw, 1024px"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Showcase section component                                        */
/* ------------------------------------------------------------------ */

export function ShowcaseSection() {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const { t } = useLingui();
  const showcaseItems: ShowcaseItem[] = [
    {
      ...showcaseItemSeeds[0],
      label: t`Cliff House`,
      title: t`Cliff House`,
      description: t`Modern cantilevered architecture perched on dramatic coastline cliffs`,
    },
    {
      ...showcaseItemSeeds[1],
      label: t`Brutalist Tower`,
      title: t`Brutalist Tower`,
      description: t`Dramatic concrete architecture with geometric forms`,
    },
    {
      ...showcaseItemSeeds[2],
      label: t`Minimalist Loft`,
      title: t`Minimalist Loft`,
      description: t`Clean Scandinavian-inspired interior with natural light`,
    },
    {
      ...showcaseItemSeeds[3],
      label: t`Glass Pavilion`,
      title: t`Glass Pavilion`,
      description: t`Transparent walls blending indoor and outdoor spaces`,
    },
    {
      ...showcaseItemSeeds[4],
      label: t`Atrium Lobby`,
      title: t`Atrium Lobby`,
      description: t`Double-height entrance with dramatic vertical space`,
    },
    {
      ...showcaseItemSeeds[5],
      label: t`Urban Courtyard`,
      title: t`Urban Courtyard`,
      description: t`Green oasis in dense urban environment`,
    },
  ];

  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
    document.body.style.overflow = "hidden";
  }, []);

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
    setActiveIndex(lightboxIndex);
    document.body.style.overflow = "";
  }, [lightboxIndex]);

  const goToPrevious = useCallback(() => {
    setLightboxIndex((prev) => (prev === 0 ? showcaseItems.length - 1 : prev - 1));
  }, []);

  const goToNext = useCallback(() => {
    setLightboxIndex((prev) => (prev === showcaseItems.length - 1 ? 0 : prev + 1));
  }, []);

  /** Keyboard handler on the dialog — avoids banned useEffect. */
  const handleDialogKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === "Escape") {
        closeLightbox();
      } else if (e.key === "ArrowLeft") {
        goToPrevious();
      } else if (e.key === "ArrowRight") {
        goToNext();
      }
    },
    [closeLightbox, goToPrevious, goToNext],
  );

  const handlePillClick = useCallback(
    (index: number) => {
      setActiveIndex(index);
      openLightbox(index);
    },
    [openLightbox],
  );

  /** Callback ref: auto-focus dialog on mount so keyboard events work. */
  const focusDialog = useCallback((node: HTMLDivElement | null) => {
    node?.focus();
  }, []);

  const lightboxItem = showcaseItems[lightboxIndex];

  return (
    <SectionShell id="showcase" ariaLabel={t`Showcase examples section`} noPadding className="py-4 sm:py-8 lg:py-12">
      <SectionContainer>
        <div className="mb-6 text-center lg:mb-10">
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-500"><Trans>Examples</Trans></p>
          <h2 className="text-2xl font-semibold text-gray-900 sm:text-3xl lg:text-4xl"><Trans>Click to explore AI-generated renders</Trans></h2>
        </div>

        {/* Isometric map with positioned pill badges */}
        <div className="relative mx-auto w-full max-w-5xl">
          <IsometricCityMap ariaLabel={t`Isometric city map illustration`} />

          {/* Pill badges positioned absolutely over the map */}
          {showcaseItems.map((item, index) => (
            <button
              key={item.id}
              type="button"
              aria-label={`${t`View`} ${item.label}`}
              aria-pressed={index === activeIndex}
              className={`absolute z-10 rounded-full px-4 py-2 text-sm font-medium shadow-md transition-all duration-200 hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                index === activeIndex
                  ? "bg-gray-900 text-white"
                  : "bg-white text-gray-900"
              }`}
              style={{
                top: item.position.top,
                left: item.position.left,
                transform: "translate(-50%, -50%)",
              }}
              onClick={() => handlePillClick(index)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </SectionContainer>

      {/* Lightbox Overlay */}
      {isLightboxOpen && (
        <div
          ref={focusDialog}
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={`${lightboxItem.title} ${t`showcase`}`}
          onKeyDown={handleDialogKeyDown}
          tabIndex={-1}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/90 backdrop-blur-sm"
            onClick={closeLightbox}
            role="presentation"
          />

          {/* Close Button */}
          <button
            className="absolute right-4 top-4 z-50 rounded-full bg-white/10 p-2 text-white backdrop-blur-sm transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 sm:right-6 sm:top-6"
            onClick={closeLightbox}
            aria-label={t`Close showcase`}
            type="button"
          >
            <XIcon className="size-6" />
          </button>

          {/* Navigation - Previous */}
          <button
            className="absolute left-4 z-50 rounded-full bg-white/10 p-3 text-white backdrop-blur-sm transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 sm:left-6"
            onClick={goToPrevious}
            aria-label={t`Previous image`}
            type="button"
          >
            <ChevronLeftIcon className="size-6" />
          </button>

          {/* Navigation - Next */}
          <button
            className="absolute right-4 z-50 rounded-full bg-white/10 p-3 text-white backdrop-blur-sm transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 sm:right-6"
            onClick={goToNext}
            aria-label={t`Next image`}
            type="button"
          >
            <ChevronRightIcon className="size-6" />
          </button>

          {/* Content */}
          <div className="relative z-40 mx-4 flex w-full max-w-6xl flex-col items-center sm:mx-8 lg:mx-12">
            {/* Image Container */}
            <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg bg-black/50">
              <Image
                alt={`${lightboxItem.title} - AI-generated architectural render`}
                className="object-contain"
                fill
                priority
                quality={95}
                sizes="(max-width: 768px) 95vw, (max-width: 1200px) 90vw, 1152px"
                src={lightboxItem.image}
              />
            </div>

            {/* Metadata */}
            <div className="mt-6 max-w-2xl text-center">
              <h3 className="mb-2 text-xl font-semibold text-white sm:text-2xl">
                <Trans>{lightboxItem.title}</Trans>
              </h3>
              <p className="text-sm text-white/80 sm:text-base">
                <Trans>{lightboxItem.description}</Trans>
              </p>
            </div>

            {/* Counter */}
            <div className="mt-4 text-sm text-white/60">
              {lightboxIndex + 1} / {showcaseItems.length}
            </div>
          </div>
        </div>
      )}
    </SectionShell>
  );
}
